import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";

// Visas service page. Frontend-only; mirrors the Flights/Stays structure with
// Visas-specific copy. CTAs route into the existing Plan a Trip intake and
// Track Trip — no new logic or dependencies.

const HELP = [
  { t: "Requirement checks", d: "What your nationality needs for your destination — confirmed up front." },
  { t: "Document guidance", d: "Clear checklists so nothing is missing when you apply." },
  { t: "Application review", d: "A careful look over your forms and documents before submission." },
  { t: "Appointment support", d: "Guidance on booking and preparing for biometrics or interviews." },
  { t: "Travel insurance guidance", d: "Help meeting cover requirements where a visa demands it." },
  { t: "Visa-linked trip planning", d: "We align your flights, stays and dates with visa timelines." },
];

const NEEDS = [
  "Tourist visa", "Business visa", "Study travel", "Family visit", "Medical travel",
  "Schengen", "UK", "USA", "Canada", "UAE", "South Africa", "Morocco",
];

const STEPS = [
  { n: "01", t: "Tell us your destination", d: "Your nationality, where you're going and your travel purpose." },
  { n: "02", t: "We check likely requirements", d: "We outline the documents and timelines you'll need." },
  { n: "03", t: "You prepare documents", d: "Work through a clear checklist at your own pace." },
  { n: "04", t: "We guide your next steps", d: "Appointments, submission and what to expect." },
];

export default function Visas() {
  return (
    <div className="page">
      <Seo
        title="Visa Guidance, Simplified — 513Travel"
        description="Get practical visa guidance for requirements, documents, appointments, travel timelines and visa-linked trip planning."
        path="/visas"
      />
      {/* ===== Hero ===== */}
      <section className="page-hero hero-split visas-hero">
        <div className="container">
          <div className="hero-split-copy">
            <div className="eyebrow">Visas</div>
            <h1>Visa guidance, simplified</h1>
            <p className="lede">
              From document checks to appointment guidance and travel timelines,
              513Travel helps you understand what you need before you apply —
              clearly, calmly and with human support.
            </p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn gold">Ask about a visa</Link>
              <Link to="/plan-a-trip" className="btn outline">Plan a full trip</Link>
            </div>
          </div>
          <figure className="hero-figure">
            <img
              src="/visas-hero.webp"
              alt="Passport, travel documents and boarding pass ready for a visa application"
              loading="eager"
              decoding="async"
            />
          </figure>
        </div>
      </section>

      {/* ===== What we help with ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">What we help with</div>
            <h2>Clarity before you apply</h2>
            <p>Practical, human guidance through the paperwork — so you apply with confidence, not confusion.</p>
          </div>
          <div className="grid grid-3">
            {HELP.map((c) => (
              <div className="service-card" key={c.t}>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Common visa needs ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">What we see often</div>
            <h2>Common visa needs</h2>
            <p>We help you prepare and understand the process — guidance and preparation, never a guarantee of approval.</p>
          </div>
          <div className="route-chips">
            {NEEDS.map((n) => (
              <span className="route-chip" key={n}>{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>How visa support works</h2>
          </div>
          <div className="grid grid-4">
            {STEPS.map((s) => (
              <div className="step-card" key={s.n}>
                <span className="step-num" aria-hidden="true">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Trust note ===== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <p className="demo-note demo-note--center">
            Visa decisions are made by embassies, consulates and immigration
            authorities. We help you prepare clearly, but approval is never
            guaranteed.
          </p>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="section section--green">
        <div className="container">
          <div className="feature-copy feature-solo">
            <div className="eyebrow">Ready to apply</div>
            <h2>Need help understanding your visa?</h2>
            <p>Share your nationality, destination and travel purpose. We'll guide you on the likely next steps.</p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn light">Ask about a visa</Link>
              <Link to="/track-trip" className="btn outline-light">Track existing trip</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
